import React, { useEffect, useState } from "react";
import { NavigationContainer } from "@react-navigation/native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import AuthStackNavigator from "./AuthStackNavigator";
import MainStackNavigator from "./MainStackNavigator";
import TutorialScreen from "../screens/TutorialScreen";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import { ActivityIndicator, View, StyleSheet, Platform } from "react-native";
import * as Linking from "expo-linking";
import Toast from "react-native-toast-message";
import { codeExchangeRequest } from "../store/slices/authSlice";
import "@/i18n";
import { useTranslation } from "react-i18next";
import { navigationRef, checkAndNavigateToReturnVendor } from "../services/NavigationService";
import { supabase } from "../lib/supabase";
import { LinkingOptions, PathConfigMap } from "@react-navigation/native";
import { PostHogProviderWrapper } from '../components/PostHogProviderWrapper';
import { PostHogHelper } from '../components/PostHogHelper';
import { useTutorial } from "../hooks/useTutorial";
import { useGuestMode } from "../contexts/GuestModeContext";
import { AuthRequiredModal } from "../components/ui/AuthRequiredModal";

import type { MainStackParamList } from './MainStackNavigator';

export type RootParamList = {
  MainApp: {
    screen?: keyof MainStackParamList;
    params?: MainStackParamList[keyof MainStackParamList];
  } | undefined;
  Tutorial: undefined;
  AuthFlow: {
    screen?: string;
    params?: { [key: string]: any };
  };
};

// Get linking prefixes based on platform
const getLinkingPrefixes = (): string[] => {
  const prefixes = ["com.jadetrade://"];
  
  // Add web prefixes
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    prefixes.push(window.location.origin + '/');
    prefixes.push(window.location.origin);
  }
  
  return prefixes;
};

// Helper to check if we should prevent URL-based navigation on web
// This prevents navigation resets when user opens external tabs (like Stripe)
const shouldPreventLinkingNavigation = (): boolean => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  
  // Check if we're in Stripe flow - don't reset navigation
  const stripeFlowFlag = sessionStorage.getItem('stripeFlowActive');
  if (stripeFlowFlag === 'true') {
    console.log('[LINKING] Preventing navigation - Stripe flow active');
    return true;
  }
  
  return false;
};

const linking: LinkingOptions<RootParamList> = {
  prefixes: getLinkingPrefixes(),
  config: {
    screens: {
      AuthFlow: {
        path: "auth",
        screens: {
          PasswordReset: "password-reset",
          Auth: "login",
        },
      } as PathConfigMap<any>,
      MainApp: {
        path: "",
        screens: {
          Vendor: "vendor/:id",
          Main: "",
        },
      } as PathConfigMap<any>,
    },
  },
  // Custom function to get initial URL and detect password reset
  getInitialURL: async () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const url = window.location.href;
      const path = window.location.pathname;
      const search = window.location.search;
      const hash = window.location.hash;
      
      // Check if this is a password reset URL
      const isPasswordReset = path.includes('password-reset') || 
                             path.includes('PasswordReset') ||
                             url.includes('type=recovery') ||
                             search.includes('type=recovery') ||
                             hash.includes('type=recovery') ||
                             url.includes('token_hash');
      
      if (isPasswordReset) {
        console.log('🔐 [PASSWORD-RESET] getInitialURL detected password reset:', { url, path, search, hash });
        // Return the URL as-is so React Navigation can process it
        return url;
      }
      
      return url;
    }
    return undefined;
  },
  // Filter function to prevent navigation to MainApp if password reset is detected
  // Also prevents navigation if we're in Stripe flow
  filter: (event) => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // CRITICAL: Prevent URL-based navigation resets during Stripe flow
      if (shouldPreventLinkingNavigation()) {
        console.log('[LINKING] Filtering out navigation event - Stripe flow active');
        return false; // Block this navigation event
      }
      
      const url = event.url || window.location.href;
      const path = window.location.pathname;
      
      // Check if this is a password reset URL
      const isPasswordReset = path.includes('password-reset') || 
                             path.includes('PasswordReset') ||
                             url.includes('type=recovery') ||
                             url.includes('token_hash');
      
      if (isPasswordReset) {
        console.log('🔐 [PASSWORD-RESET] Filter detected password reset URL, allowing navigation:', url);
        return true; // Allow this navigation
      }
    }
    return true; // Allow all other navigations
  },
};

const Stack = createNativeStackNavigator();

export default function MainSwitchNavigator() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // Track Stripe flow status for preventing unwanted navigation
  const [isStripeFlowActive, setIsStripeFlowActive] = useState(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return sessionStorage.getItem('stripeFlowActive') === 'true';
    }
    return false;
  });
  
  // Periodically check the Stripe flow flag
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    
    const checkStripeFlow = () => {
      const isActive = sessionStorage.getItem('stripeFlowActive') === 'true';
      if (isActive !== isStripeFlowActive) {
        console.log('💳 [STRIPE] Flow active status changed:', isActive);
        setIsStripeFlowActive(isActive);
      }
    };
    
    const interval = setInterval(checkStripeFlow, 500);
    return () => clearInterval(interval);
  }, [isStripeFlowActive]);
  
  // Always call the hook, but we'll ignore its value during Stripe flow
  const rawUrl = Linking.useURL();
  const url = isStripeFlowActive ? null : rawUrl;

  const isAuthenticated = useSelector((state: RootState) => !!state.auth.user);
  const { initialized, loading } = useSelector(
    (state: RootState) => state.auth
  );
  
  // Get guest mode state
  const { isGuestMode } = useGuestMode();
  
  // Track if another tab is doing password reset (we should stay on auth)
  const [ignoreAuthFromOtherTab, setIgnoreAuthFromOtherTab] = useState(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Check sessionStorage first
      const ignoreFlag = sessionStorage.getItem('ignoreAuthChanges');
      if (ignoreFlag === 'true') {
        console.log('🔐 [PASSWORD-RESET] Initial state: ignoreAuthChanges flag found - another tab is doing password reset');
        return true;
      }
      // ALSO check localStorage directly for password reset from another tab
      // This handles the race condition where the storage event hasn't fired yet
      const anotherTabDoingPasswordReset = localStorage.getItem('jadetrade_password_reset');
      const thisTabDoingPasswordReset = sessionStorage.getItem('passwordResetFlow') === 'true';
      if (anotherTabDoingPasswordReset && !thisTabDoingPasswordReset) {
        console.log('🔐 [PASSWORD-RESET] Initial state: Another tab is doing password reset (from localStorage)');
        sessionStorage.setItem('ignoreAuthChanges', 'true');
        return true;
      }
    }
    return false;
  });
  
  // Listen for changes to ignoreAuthChanges flag (set by App.tsx storage event handler)
  // Also check localStorage directly for the password reset flag
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const checkIgnoreFlag = () => {
        const ignoreFlag = sessionStorage.getItem('ignoreAuthChanges') === 'true';
        // Also check localStorage directly for password reset from another tab
        const anotherTabDoingPasswordReset = localStorage.getItem('jadetrade_password_reset');
        const thisTabDoingPasswordReset = sessionStorage.getItem('passwordResetFlow') === 'true';
        const shouldIgnore = ignoreFlag || (anotherTabDoingPasswordReset && !thisTabDoingPasswordReset);
        
        if (shouldIgnore !== ignoreAuthFromOtherTab) {
          console.log('🔐 [PASSWORD-RESET] ignoreAuthFromOtherTab changed to:', shouldIgnore);
          setIgnoreAuthFromOtherTab(shouldIgnore);
          // Also set sessionStorage if not already set
          if (shouldIgnore && !ignoreFlag) {
            sessionStorage.setItem('ignoreAuthChanges', 'true');
          }
        }
      };
      
      // Check immediately
      checkIgnoreFlag();
      
      // Check periodically (sessionStorage changes don't trigger events in the same tab)
      const interval = setInterval(checkIgnoreFlag, 500);
      
      return () => clearInterval(interval);
    }
  }, [ignoreAuthFromOtherTab]);
  
  // Track if we're viewing a public vendor profile (allow unauthenticated access)
  const [isPublicVendorView, setIsPublicVendorView] = useState(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const path = window.location.pathname;
      // Check if we're on a vendor profile URL
      const isVendorUrl = path.startsWith('/vendor/') && path.length > '/vendor/'.length;
      if (isVendorUrl) {
        console.log('🏪 [PUBLIC-VENDOR] Detected vendor URL, allowing public access:', path);
        return true;
      }
    }
    return false;
  });

  // Listen for navigateToAuth flag (set by NavigationService.navigateToAuth)
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    
    const checkNavigateToAuth = () => {
      const flag = sessionStorage.getItem('navigateToAuth');
      if (flag === 'true') {
        console.log('🏪 [PUBLIC-VENDOR] navigateToAuth flag detected, clearing public vendor view');
        sessionStorage.removeItem('navigateToAuth');
        setIsPublicVendorView(false);
      }
    };
    
    // Check immediately
    checkNavigateToAuth();
    
    // Check periodically
    const interval = setInterval(checkNavigateToAuth, 100);
    
    return () => clearInterval(interval);
  }, []);

  // Track if we're in password reset flow (even if authenticated)
  // Check URL on mount to detect password reset flow
  // Also check session storage for password reset flag (set by App.tsx)
  const [isPasswordResetFlow, setIsPasswordResetFlow] = useState(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // CRITICAL: Check sessionStorage FIRST - this is set by App.tsx early detection
      // and persists even after Supabase clears the URL hash
      const sessionFlag = sessionStorage.getItem('passwordResetFlow');
      if (sessionFlag === 'true') {
        console.log('🔐 [PASSWORD-RESET] Initial state: Flag found in sessionStorage');
        return true;
      }
      
      const path = window.location.pathname;
      const search = window.location.search;
      const hash = window.location.hash;
      const fullUrl = window.location.href;
      
      // Check if we're on a password reset URL
      const isPasswordReset = path.includes('password-reset') || 
             path.includes('PasswordReset') ||
             fullUrl.includes('type=recovery') ||
             search.includes('type=recovery') ||
             hash.includes('type=recovery') ||
             fullUrl.includes('token_hash');
      
      if (isPasswordReset) {
        console.log('🔐 [PASSWORD-RESET] Initial state: Password reset detected from URL', {
          path,
          search,
          hash,
          fullUrl
        });
        // Store in sessionStorage so it persists
        sessionStorage.setItem('passwordResetFlow', 'true');
      }
      
      return isPasswordReset;
    }
    return false;
  });
  
  // Check tutorial status for authenticated users
  const { shouldShowTutorial, isChecking: isCheckingTutorial } = useTutorial();

  // Track processed URLs to prevent repeated handling
  const [processedUrls] = useState(() => new Set<string>());
  
  useEffect(() => {
    const handleUrl = async (event: { url: string }) => {
      if (!event.url || typeof event.url !== 'string') {
        return;
      }

      // Skip if we've already processed this URL (prevents log spam)
      if (processedUrls.has(event.url)) {
        return;
      }
      
      // On web, skip URL processing if we're in Stripe flow to prevent navigation issues
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const stripeFlowActive = sessionStorage.getItem('stripeFlowActive') === 'true';
        if (stripeFlowActive) {
          console.log('[LINKING] Skipping URL processing - Stripe flow active');
          return;
        }
      }

      try {
        // On web, check if this is a password reset URL and set flag immediately
        if (Platform.OS === 'web') {
          const isPasswordResetUrl = event.url.includes('password-reset') || 
                                    event.url.includes('PasswordReset') ||
                                    event.url.includes('type=recovery') ||
                                    event.url.includes('token_hash');
          
          if (isPasswordResetUrl && !isPasswordResetFlow) {
            console.log('🔐 [PASSWORD-RESET] Detected password reset URL in handleUrl, setting flag');
            setIsPasswordResetFlow(true);
          }
          
          try {
            // Try to parse as absolute URL
            const urlObj = new URL(event.url);
            const hasAuthParams = urlObj.searchParams.has('access_token') || 
                                 urlObj.searchParams.has('refresh_token') || 
                                 urlObj.searchParams.has('token_hash') || 
                                 urlObj.searchParams.has('code') ||
                                 urlObj.searchParams.has('type');
            
            // If it's a web URL without auth params and not password reset, skip
            if (!hasAuthParams && !urlObj.hash.includes('access_token') && !urlObj.hash.includes('token_hash')) {
              // Mark as processed to prevent repeated calls
              processedUrls.add(event.url);
              return;
            }
          } catch (e) {
            // If URL parsing fails (e.g., relative path), check if it contains auth params in the string
            const hasAuthParams = event.url.includes('access_token') || 
                                 event.url.includes('refresh_token') || 
                                 event.url.includes('token_hash') || 
                                 event.url.includes('code=') ||
                                 (event.url.includes('type=') && (event.url.includes('recovery') || event.url.includes('signup')));
            
            // If it's a relative path without auth params, skip it
            if (!hasAuthParams) {
              // Mark as processed to prevent repeated calls
              processedUrls.add(event.url);
              return;
            }
          }
        }

        console.log('🔗 Handling Supabase password recovery deep link:', event.url);
        
        // Mark as processed
        processedUrls.add(event.url);

        // Extract parameters from either ?query or #hash fragment
        const rawParams = event.url.split('#')[1] || event.url.split('?')[1];
        if (!rawParams) {
          return;
        }

        const params = new URLSearchParams(rawParams);
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const type = params.get('type');
        const code = params.get('code') || params.get('token_hash');

        if (type === 'recovery' && accessToken && refreshToken) {
          console.log('🔗 Supabase implicit recovery link detected — setting session');
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error('❌ Failed to set Supabase session from implicit link:', error);
            Toast.show({
              type: 'error',
              text1: 'Link expired or invalid',
              text2: 'Please request a new password reset link.',
            });
            return;
          }

          console.log('✅ Supabase recovery session established via implicit flow');
          // Mark that we're in password reset flow
          setIsPasswordResetFlow(true);
          // Use reset to ensure we're on the AuthFlow stack even if user is authenticated
          navigationRef.reset({
            index: 0,
            routes: [
              {
                name: 'AuthFlow',
                params: {
                  screen: 'PasswordReset'
                }
              }
            ],
          });
          return;
        }

        // Fallback for OTP-style recovery link (?code=...)
        if (code) {
          console.log('🔗 Supabase OTP recovery link detected — verifying OTP');
          const { data, error } = await supabase.auth.verifyOtp({
            type: 'recovery',
            token_hash: code,
          });

          if (error && !data?.session) {
            console.error('❌ Supabase verifyOtp failed:', error);
            Toast.show({
              type: 'error',
              text1: 'Link expired or invalid',
              text2: 'Please request a new password reset link.',
            });
            return;
          }

          console.log('✅ Recovery session established via OTP:', data.session);
          // Mark that we're in password reset flow
          setIsPasswordResetFlow(true);
          // Use reset to ensure we're on the AuthFlow stack even if user is authenticated
          navigationRef.reset({
            index: 0,
            routes: [
              {
                name: 'AuthFlow',
                params: {
                  screen: 'PasswordReset'
                }
              }
            ],
          });
        }
      } catch (err) {
        console.error('Failed to handle deep link:', event.url, err);
      }
    };

    const subscription = Linking.addEventListener("url", handleUrl);

    // Only handle URL if it exists and is a valid string AND we're not in Stripe flow
    if (url && typeof url === 'string' && !isStripeFlowActive) {
      handleUrl({ url });
    }

    return () => {
      subscription.remove();
    };
  }, [dispatch, url, t, isStripeFlowActive]);

  // Navigate to PasswordReset screen when password reset flow is detected
  useEffect(() => {
    if (isPasswordResetFlow) {
      console.log('🔐 [PASSWORD-RESET] Flag is true, setting up navigation...');
      
      const navigateToPasswordReset = () => {
        // CRITICAL: Check if password reset was completed - don't navigate back
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const completedFlag = sessionStorage.getItem('passwordResetCompleted');
          if (completedFlag === 'true') {
            console.log('🔐 [PASSWORD-RESET] Password reset completed, aborting navigation');
            return;
          }
          
          const sessionFlag = sessionStorage.getItem('passwordResetFlow');
          if (sessionFlag !== 'true') {
            console.log('🔐 [PASSWORD-RESET] SessionStorage flag cleared, aborting navigation');
            return;
          }
          
          // Don't navigate during Stripe flow
          const stripeFlowActive = sessionStorage.getItem('stripeFlowActive') === 'true';
          if (stripeFlowActive) {
            console.log('💳 [STRIPE] Skipping password reset navigation - Stripe flow active');
            return;
          }
        }
        
        if (!navigationRef.isReady()) {
          console.log('🔐 [PASSWORD-RESET] Navigation not ready, retrying in 100ms...');
          setTimeout(navigateToPasswordReset, 100);
          return;
        }

        try {
          const currentState = navigationRef.getRootState();
          console.log('🔐 [PASSWORD-RESET] Current navigation state:', {
            routes: currentState?.routes?.map(r => r.name),
            index: currentState?.index
          });
          
          console.log('🔐 [PASSWORD-RESET] Attempting to navigate to PasswordReset');
          // Always reset to ensure we're on the right screen
          navigationRef.reset({
            index: 0,
            routes: [
              {
                name: 'AuthFlow',
                params: {
                  screen: 'PasswordReset'
                }
              }
            ],
          });
          
          // Verify navigation worked
          setTimeout(() => {
            const newState = navigationRef.getRootState();
            const newRoute = newState?.routes[newState.index];
            console.log('🔐 [PASSWORD-RESET] After navigation:', {
              routeName: newRoute?.name,
              authFlowState: newRoute?.state,
              authFlowRoutes: newRoute?.state?.routes?.map((r: any) => r.name)
            });
          }, 100);
          
          console.log('🔐 [PASSWORD-RESET] Navigation reset completed');
        } catch (error) {
          console.error('🔐 [PASSWORD-RESET] Error navigating to PasswordReset:', error);
        }
      };

      // Small delay to ensure AuthFlow is mounted (reduced from 500ms)
      const timer = setTimeout(navigateToPasswordReset, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isPasswordResetFlow]);

  // Clear password reset flow flag when navigating away from PasswordReset
  useEffect(() => {
    if (!navigationRef.isReady()) return;

    const unsubscribe = navigationRef.addListener('state', (e) => {
      // Skip during Stripe flow to prevent any navigation interference
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const stripeFlowActive = sessionStorage.getItem('stripeFlowActive') === 'true';
        if (stripeFlowActive) {
          return; // Don't process navigation state changes during Stripe flow
        }
      }
      
      const state = e.data?.state;
      const routes = state?.routes || [];
      const currentRoute = routes[routes.length - 1];
      
      // Check if we're navigating away from PasswordReset
      if (currentRoute?.name === 'AuthFlow') {
        const authFlowState = currentRoute.state;
        const authFlowRoutes = authFlowState?.routes || [];
        const currentAuthScreen = authFlowRoutes[authFlowRoutes.length - 1];
        
        // CRITICAL: Only clear flag if we've navigated AWAY from PasswordReset
        // Don't clear just because we're on a different screen during initial load
        // The flag should only be cleared when password reset is COMPLETED or user explicitly navigates away
        // Check if URL still indicates password reset - if so, don't clear
        if (currentAuthScreen?.name !== 'PasswordReset' && isPasswordResetFlow) {
          const urlStillIndicatesPasswordReset = Platform.OS === 'web' && typeof window !== 'undefined' &&
            (window.location.pathname.includes('password-reset') || 
             window.location.pathname.includes('PasswordReset'));
          
          if (!urlStillIndicatesPasswordReset) {
            console.log('🔐 [PASSWORD-RESET] Navigated away from PasswordReset, clearing flag');
            setIsPasswordResetFlow(false);
            // Clear sessionStorage
            if (Platform.OS === 'web' && typeof window !== 'undefined') {
              sessionStorage.removeItem('passwordResetFlow');
            }
          } else {
            console.log('🔐 [PASSWORD-RESET] URL still indicates password reset, NOT clearing flag');
          }
        }
      } else if (currentRoute?.name === 'MainApp' && isPasswordResetFlow) {
        // If we navigated to MainApp, check if we should still be in password reset flow
        // (might have been redirected by React Navigation before flag was set)
        const sessionFlag = Platform.OS === 'web' && typeof window !== 'undefined' 
          ? sessionStorage.getItem('passwordResetFlow') 
          : null;
        
        if (sessionFlag === 'true') {
          console.log('🔐 [PASSWORD-RESET] Still in password reset flow (sessionStorage), redirecting to AuthFlow');
          // Redirect back to AuthFlow -> PasswordReset
          navigationRef.reset({
            index: 0,
            routes: [
              {
                name: 'AuthFlow',
                params: {
                  screen: 'PasswordReset'
                }
              }
            ],
          });
        } else {
          console.log('🔐 [PASSWORD-RESET] Navigated to MainApp, clearing flag');
          setIsPasswordResetFlow(false);
        }
      }
    });

    return unsubscribe;
  }, [isPasswordResetFlow]);

  // After user signs in, check if they should return to a vendor page
  useEffect(() => {
    if (isAuthenticated && Platform.OS === 'web' && typeof window !== 'undefined') {
      // Small delay to ensure navigation is ready
      const timer = setTimeout(() => {
        const didNavigate = checkAndNavigateToReturnVendor();
        if (didNavigate) {
          console.log('🏪 [RETURN-VENDOR] Redirected user back to vendor after sign in');
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  // Clear public vendor view when user navigates away from vendor page (while unauthenticated)
  useEffect(() => {
    if (!navigationRef.isReady() || isAuthenticated) return;

    const unsubscribe = navigationRef.addListener('state', (e) => {
      if (Platform.OS === 'web' && typeof window !== 'undefined' && isPublicVendorView) {
        const path = window.location.pathname;
        const isStillOnVendor = path.startsWith('/vendor/');
        
        if (!isStillOnVendor) {
          console.log('🏪 [PUBLIC-VENDOR] User navigated away from vendor page, clearing public view flag');
          setIsPublicVendorView(false);
        }
      }
    });

    return unsubscribe;
  }, [isPublicVendorView, isAuthenticated]);

  // Log navigation state for debugging
  useEffect(() => {
    if (Platform.OS === 'web') {
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : 'N/A';
      const currentUrl = typeof window !== 'undefined' ? window.location.href : 'N/A';
      const shouldShowMain = (isAuthenticated && !isPasswordResetFlow && !ignoreAuthFromOtherTab) || isPublicVendorView || isGuestMode;
      const showing = shouldShowMain ? 'MainApp' : 'AuthFlow';
      console.log('🔐 [NAVIGATION] State:', {
        isAuthenticated: String(isAuthenticated),
        isPasswordResetFlow: String(isPasswordResetFlow),
        ignoreAuthFromOtherTab: String(ignoreAuthFromOtherTab),
        isPublicVendorView: String(isPublicVendorView),
        isGuestMode: String(isGuestMode),
        showing: String(showing),
        currentPath: String(currentPath),
        currentUrl: String(currentUrl),
        navigationReady: String(navigationRef.isReady())
      });
      
      // Check if URL should trigger password reset
      const shouldBePasswordReset = currentPath.includes('password-reset') || 
                                    currentPath.includes('PasswordReset') ||
                                    currentUrl.includes('type=recovery') ||
                                    currentUrl.includes('token_hash');
      
      // Check if password reset was completed - don't set any flags
      const completedFlag = typeof window !== 'undefined' ? sessionStorage.getItem('passwordResetCompleted') : null;
      if (completedFlag === 'true') {
        console.log('🔐 [PASSWORD-RESET] Password reset completed, not setting any flags');
        return; // Early return - don't do anything else
      }
      
      // Check sessionStorage for password reset flag (set by App.tsx)
      // CRITICAL: Only set the flag if URL also indicates password reset!
      const sessionFlag = typeof window !== 'undefined' ? sessionStorage.getItem('passwordResetFlow') : null;
      if (sessionFlag === 'true' && !isPasswordResetFlow && shouldBePasswordReset) {
        console.log('🔐 [PASSWORD-RESET] Flag found in sessionStorage AND URL indicates password reset, setting state flag');
        setIsPasswordResetFlow(true);
      }
      
      // Only set flag if URL indicates password reset AND sessionStorage also has the flag
      // This prevents re-setting the flag after password update when sessionStorage was cleared
      if (shouldBePasswordReset && !isPasswordResetFlow && sessionFlag === 'true') {
        console.warn('🔐 [PASSWORD-RESET] ⚠️ URL indicates password reset but state flag is FALSE (sessionStorage has it)', {
          currentPath: String(currentPath),
          currentUrl: String(currentUrl),
          isPasswordResetFlow: String(isPasswordResetFlow),
          sessionFlag: String(sessionFlag)
        });
        // Set the state flag to match sessionStorage
        console.log('🔐 [PASSWORD-RESET] Setting state flag to match sessionStorage...');
        setIsPasswordResetFlow(true);
      }
    }
  }, [isAuthenticated, isPasswordResetFlow]);
  
  // Check sessionStorage periodically for password reset flag
  // This catches cases where the flag is set after initial render
  // IMPORTANT: Only set the flag if URL actually indicates password reset, not during checkout!
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    
    const checkSessionStorageFlag = () => {
      // Don't check during Stripe flow
      const stripeFlowActive = sessionStorage.getItem('stripeFlowActive') === 'true';
      if (stripeFlowActive) {
        return;
      }
      
      // Don't set flag if password reset was completed
      const completedFlag = sessionStorage.getItem('passwordResetCompleted');
      if (completedFlag === 'true') {
        return;
      }
      
      // CRITICAL: Only set the flag if the URL actually indicates password reset
      // This prevents stale sessionStorage flags from navigating away during checkout
      const currentPath = window.location.pathname;
      const currentUrl = window.location.href;
      const isPasswordResetUrl = currentPath.includes('password-reset') || 
                                  currentPath.includes('PasswordReset') ||
                                  currentUrl.includes('type=recovery') ||
                                  currentUrl.includes('token_hash');
      
      if (!isPasswordResetUrl) {
        // Not on a password reset URL - don't set the flag even if sessionStorage has it
        return;
      }
      
      const sessionFlag = sessionStorage.getItem('passwordResetFlow');
      if (sessionFlag === 'true' && !isPasswordResetFlow) {
        console.log('🔐 [PASSWORD-RESET] Interval check: Flag found in sessionStorage AND URL indicates password reset, setting state flag');
        setIsPasswordResetFlow(true);
      }
    };
    
    // Check immediately
    checkSessionStorageFlag();
    
    // Check every 200ms for a short period
    const interval = setInterval(checkSessionStorageFlag, 200);
    
    // Stop checking after 3 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 3000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isPasswordResetFlow]);

  // All hooks must be called before conditional returns
  if (!initialized && loading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  // Show loading while checking tutorial status
  if (isAuthenticated && isCheckingTutorial) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  return (
    <NavigationContainer ref={navigationRef} linking={linking}>
      {/* PostHogProvider must be INSIDE NavigationContainer for navigation tracking to work */}
      <PostHogProviderWrapper
        apiKey="phc_d0vjpFekmKvyVYGQL0YIiYZbJ6pMsHLSCzeuq1rt7cS"
        options={{
          host: 'https://us.i.posthog.com',
          enableSessionReplay: true,
        }}
        autocapture
      >
        <PostHogHelper />
        <Stack.Navigator screenOptions={{ headerShown: false }}>
          {(isAuthenticated && !isPasswordResetFlow && !ignoreAuthFromOtherTab) || isPublicVendorView || isGuestMode ? (
            <Stack.Screen name="MainApp" component={MainStackNavigator} />
          ) : (
            <Stack.Screen name="AuthFlow" component={AuthStackNavigator} />
          )}
        </Stack.Navigator>
        <AuthRequiredModal />
      </PostHogProviderWrapper>
    </NavigationContainer>
  );
}

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});
